import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
	ClipboardPen,
	Printer,
	Mail,
	Loader as Loader2,
	ChevronDown,
	ChevronRight,
	RefreshCw,
	Pencil,
	Save,
	X,
} from 'lucide-react'
import { Button } from '../ui/Button'
import { useAuth } from '../../contexts/AuthContext'
import { getAdminId } from '../../utils/getAdminId'
import { supabase } from '../../lib/supabase'
import { fetchCompanyProfile, type CompanyProfile } from '../../lib/companyProfile'
import {
	buildShopStockCountMatrix,
	buildShopStockCountReportHtml,
	printShopStockCountReport,
	type ShopStockCountReportLine,
} from '../shop-stock-count/shop-stock-count-report'
import {
	fetchWorkerContacts,
	isEmailConfigured,
	recordShopStockCountDelivery,
	sendShopStockCountReport,
} from '../../lib/shopStockCountEmail'

interface ShopStockCountSubmission {
	count_id: string
	count_number: string
	status: string
	mode: 'one_by_one' | 'barcode' | 'uhf'
	location_name: string | null
	started_at: string
	started_by_name: string | null
	submitted_at: string | null
	submitted_by_name: string | null
	report_email_to: string | null
	report_email_sent_at: string | null
	report_email_error: string | null
	report_printed_at: string | null
	line_count: number
	unit_count: number
	lines: ShopStockCountReportLine[]
}

function toIsoStart(value: string): string {
	return new Date(`${value}T00:00:00`).toISOString()
}

function toIsoEndExclusive(value: string): string {
	const date = new Date(`${value}T00:00:00`)
	date.setDate(date.getDate() + 1)
	return date.toISOString()
}

function defaultFrom(): string {
	const date = new Date()
	date.setDate(date.getDate() - 30)
	return date.toISOString().slice(0, 10)
}

/**
 * Submitted shop stock counts. Quantities can be edited with an audit trail;
 * print and email reuse the same matrix HTML as the counting page.
 */
export const ShopStockQtyReport: React.FC = () => {
	const { user } = useAuth()
	const adminId = useMemo(() => (user ? getAdminId(user) : null), [user])

	const [from, setFrom] = useState(defaultFrom())
	const [to, setTo] = useState(() => new Date().toISOString().slice(0, 10))
	const [rows, setRows] = useState<ShopStockCountSubmission[]>([])
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [expanded, setExpanded] = useState<Set<string>>(new Set())
	const [company, setCompany] = useState<CompanyProfile | null>(null)
	const [editingId, setEditingId] = useState<string | null>(null)
	const [draftQtys, setDraftQtys] = useState<Record<string, number>>({})
	const [saving, setSaving] = useState(false)
	const [emailBusyId, setEmailBusyId] = useState<string | null>(null)

	const load = useCallback(async () => {
		if (!adminId) return
		setLoading(true)
		setError(null)
		try {
			const { data, error: rpcError } = await supabase.rpc('get_shop_stock_counts', {
				p_admin_id: adminId,
				p_status: 'submitted',
				p_from: toIsoStart(from),
				p_to: toIsoEndExclusive(to),
				p_limit: 500,
			})
			if (rpcError) throw rpcError
			setRows((data ?? []) as ShopStockCountSubmission[])
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Could not load shop stock counts')
		} finally {
			setLoading(false)
		}
	}, [adminId, from, to])

	useEffect(() => {
		void load()
	}, [load])

	useEffect(() => {
		if (!adminId) return
		let cancelled = false
		void fetchCompanyProfile(adminId).then((profile) => {
			if (!cancelled) setCompany(profile)
		})
		return () => {
			cancelled = true
		}
	}, [adminId])

	const toggle = (id: string) => {
		setExpanded((prev) => {
			const next = new Set(prev)
			if (next.has(id)) next.delete(id)
			else next.add(id)
			return next
		})
	}

	const startEdit = (row: ShopStockCountSubmission) => {
		const draft: Record<string, number> = {}
		for (const line of row.lines) {
			if (line.id) draft[line.id] = line.counted_qty
		}
		setDraftQtys(draft)
		setEditingId(row.count_id)
		setExpanded((prev) => new Set(prev).add(row.count_id))
	}

	const saveEdit = async (row: ShopStockCountSubmission) => {
		if (!adminId || saving) return
		setSaving(true)
		setError(null)
		try {
			for (const line of row.lines) {
				if (!line.id) continue
				const nextQty = draftQtys[line.id]
				if (nextQty == null || nextQty === line.counted_qty) continue
				const { data, error: rpcError } = await supabase.rpc('set_shop_stock_count_line_qty', {
					p_admin_id: adminId,
					p_count_id: row.count_id,
					p_line_id: line.id,
					p_user_id: user?.id ?? null,
					p_counted_qty: nextQty,
				})
				if (rpcError) throw rpcError
				const res = data as { success: boolean; error?: string }
				if (!res.success) throw new Error(res.error || 'Could not save quantity')
			}
			setEditingId(null)
			await load()
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Could not save edits')
		} finally {
			setSaving(false)
		}
	}

	const reprint = (row: ShopStockCountSubmission) => {
		if (!adminId) return
		const html = buildShopStockCountReportHtml({
			countNumber: row.count_number,
			mode: row.mode,
			locationName: row.location_name,
			startedByName: row.started_by_name,
			submittedByName: row.submitted_by_name,
			submittedAt: row.submitted_at || row.started_at,
			lines: row.lines,
			company,
		})
		printShopStockCountReport(html)
		void recordShopStockCountDelivery({
			adminId,
			countId: row.count_id,
			printed: true,
		})
	}

	const resend = async (row: ShopStockCountSubmission) => {
		if (!adminId) return
		setEmailBusyId(row.count_id)
		try {
			const configured = await isEmailConfigured(adminId)
			if (!configured) {
				setError('Email is not set up for this company. Use Print instead.')
				return
			}
			const contacts = await fetchWorkerContacts(adminId)
			const to = row.report_email_to || user?.email || contacts[0]?.email || ''
			if (!to) {
				setError('No recipient email available.')
				return
			}
			const html = buildShopStockCountReportHtml({
				countNumber: row.count_number,
				mode: row.mode,
				locationName: row.location_name,
				startedByName: row.started_by_name,
				submittedByName: row.submitted_by_name,
				submittedAt: row.submitted_at || row.started_at,
				lines: row.lines,
				company,
			})
			await sendShopStockCountReport({
				adminId,
				to,
				reportHtml: html,
				summary: {
					countNumber: row.count_number,
					locationName: row.location_name,
					countedByName: row.submitted_by_name,
					lines: row.lines,
				},
			})
			await recordShopStockCountDelivery({ adminId, countId: row.count_id, to })
			await load()
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Email failed')
		} finally {
			setEmailBusyId(null)
		}
	}

	return (
		<div className="space-y-6">
			<div className="flex flex-wrap items-end gap-3">
				<div>
					<label className="block text-xs font-medium text-gray-500 mb-1">From</label>
					<input
						type="date"
						value={from}
						onChange={(e) => setFrom(e.target.value)}
						className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
					/>
				</div>
				<div>
					<label className="block text-xs font-medium text-gray-500 mb-1">To</label>
					<input
						type="date"
						value={to}
						onChange={(e) => setTo(e.target.value)}
						className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
					/>
				</div>
				<Button variant="secondary" onClick={() => void load()} disabled={loading}>
					<RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
					Refresh
				</Button>
			</div>

			{error && (
				<div className="rounded-lg border border-red-200 bg-red-50 text-red-900 px-4 py-3 text-sm">
					{error}
				</div>
			)}

			{loading && rows.length === 0 ? (
				<div className="flex items-center justify-center py-16 text-gray-500 gap-2">
					<Loader2 className="h-5 w-5 animate-spin" /> Loading…
				</div>
			) : rows.length === 0 ? (
				<div className="rounded-2xl border border-dashed border-gray-300 dark:border-gray-700 py-16 text-center text-gray-500 text-sm">
					No submitted shop stock counts in this range
				</div>
			) : (
				<div className="space-y-3">
					{rows.map((row) => {
						const isOpen = expanded.has(row.count_id)
						const isEditing = editingId === row.count_id
						const matrix = buildShopStockCountMatrix(
							isEditing
								? row.lines.map((l) =>
										l.id && draftQtys[l.id] != null
											? { ...l, counted_qty: draftQtys[l.id] }
											: l
								  )
								: row.lines
						)

						return (
							<div
								key={row.count_id}
								className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden"
							>
								<button
									type="button"
									onClick={() => toggle(row.count_id)}
									className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800/60"
								>
									{isOpen ? (
										<ChevronDown className="h-4 w-4 text-gray-400" />
									) : (
										<ChevronRight className="h-4 w-4 text-gray-400" />
									)}
									<ClipboardPen className="h-4 w-4 text-teal-700" />
									<div className="flex-1 min-w-0">
										<div className="font-semibold text-gray-900 dark:text-gray-100">
											{row.count_number}
										</div>
										<div className="text-xs text-gray-500">
											{row.location_name || 'Shop'} · {row.unit_count} units ·{' '}
											{row.submitted_by_name || '—'} ·{' '}
											{row.submitted_at
												? new Date(row.submitted_at).toLocaleString()
												: '—'}
										</div>
									</div>
								</button>

								{isOpen && (
									<div className="border-t border-gray-100 dark:border-gray-800 px-4 py-4 space-y-4">
										<div className="flex flex-wrap gap-2">
											{!isEditing ? (
												<Button variant="secondary" onClick={() => startEdit(row)}>
													<Pencil className="h-4 w-4 mr-1" /> Edit quantities
												</Button>
											) : (
												<>
													<Button
														variant="primary"
														onClick={() => void saveEdit(row)}
														disabled={saving}
													>
														{saving ? (
															<Loader2 className="h-4 w-4 animate-spin mr-1" />
														) : (
															<Save className="h-4 w-4 mr-1" />
														)}
														Save
													</Button>
													<Button
														variant="secondary"
														onClick={() => setEditingId(null)}
														disabled={saving}
													>
														<X className="h-4 w-4 mr-1" /> Cancel
													</Button>
												</>
											)}
											<Button variant="secondary" onClick={() => reprint(row)}>
												<Printer className="h-4 w-4 mr-1" /> Print
											</Button>
											<Button
												variant="secondary"
												onClick={() => void resend(row)}
												disabled={emailBusyId === row.count_id}
											>
												{emailBusyId === row.count_id ? (
													<Loader2 className="h-4 w-4 animate-spin mr-1" />
												) : (
													<Mail className="h-4 w-4 mr-1" />
												)}
												Email
											</Button>
										</div>

										{isEditing ? (
											<div className="space-y-2">
												{row.lines.map((line) => (
													<div
														key={line.id}
														className="flex items-center gap-3 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2"
													>
														<div className="flex-1 min-w-0">
															<div className="text-sm font-medium truncate">
																{line.product_name || line.sku}
															</div>
															<div className="text-xs text-gray-500">
																{[line.color_name, line.size_name].filter(Boolean).join(' / ')}
																{line.edited_by_name
																	? ` · edited by ${line.edited_by_name}`
																	: ''}
															</div>
														</div>
														<input
															type="number"
															min={0}
															value={line.id ? draftQtys[line.id] ?? line.counted_qty : 0}
															onChange={(e) => {
																if (!line.id) return
																setDraftQtys((prev) => ({
																	...prev,
																	[line.id!]: Math.max(0, Number(e.target.value) || 0),
																}))
															}}
															className="w-20 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1 text-sm text-center"
														/>
													</div>
												))}
											</div>
										) : (
											<div className="overflow-x-auto">
												<table className="min-w-full text-sm">
													<thead className="bg-teal-50 dark:bg-teal-900/30">
														<tr>
															<th className="px-3 py-2 text-left">Product</th>
															<th className="px-3 py-2 text-left">Color</th>
															{matrix.sizeColumns.map((size) => (
																<th key={size} className="px-2 py-2 text-center">
																	{size}
																</th>
															))}
															<th className="px-3 py-2 text-center">Total</th>
														</tr>
													</thead>
													<tbody>
														{matrix.groups.map((group) =>
															group.colors.map((color, idx) => (
																<tr
																	key={`${group.productName}-${color.colorId}`}
																	className="border-t border-gray-100 dark:border-gray-800"
																>
																	{idx === 0 && (
																		<td
																			className="px-3 py-2 font-medium align-top"
																			rowSpan={group.colors.length}
																		>
																			{group.productName}
																		</td>
																	)}
																	<td className="px-3 py-2">{color.colorName}</td>
																	{matrix.sizeColumns.map((size) => (
																		<td key={size} className="px-2 py-2 text-center font-semibold">
																			{color.sizeQuantities[size] || '·'}
																		</td>
																	))}
																	<td className="px-3 py-2 text-center font-bold">
																		{color.totalQty}
																	</td>
																</tr>
															))
														)}
													</tbody>
												</table>
												{row.lines.some((l) => l.edited_at) && (
													<p className="text-xs text-amber-700 mt-2">
														Some quantities were edited after submission.
													</p>
												)}
											</div>
										)}
									</div>
								)}
							</div>
						)
					})}
				</div>
			)}
		</div>
	)
}
